/**
 * Financials — the CFO view. One question: "Where's your cash — and how fast
 * does it come back?"
 *
 * Profit and cash are different animals: you pay for stock ~a lead-time before
 * it sells, you pay to win a customer before they've paid you back, and
 * overhead bills monthly regardless. This page tells that story in order:
 *
 *  1. Heroes — operating profit (what the window actually earned after ALL
 *     costs), Capital Return Velocity (the signature: how long a dollar is
 *     gone), and the Profit Inflection Point (true monthly break-even).
 *  2. Cash cycle & payback — ONE card: the dollar's round trip up top (the
 *     cash-cycle strip + dollar-days rule), then the SAME payback stretch broken
 *     into layers (inventory float on cash, acquisition, overhead) — line and
 *     cost bar share one window, so this and the Exec curve tell one story.
 *  3. The profit waterfall + monthly trends (operating profit incl. loss
 *     months, revenue vs break-even on ONE shared dollar scale).
 *  4. Reinvestment capacity — the honest version of "how much more can I
 *     spend?".
 *
 * Numbers we CAN'T compute honestly (bank balance, runway, AP/AR) are absent on
 * purpose — nothing here is faked from data we don't have, and a metric whose
 * queries haven't resolved shows "—", never a number missing one of its costs
 * (review 2026-07-04). Blended spend and overhead aren't tracked per product,
 * so the whole page is company-wide (it has no product rail).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { usePeriod } from "../lib/period.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { Card, CardHead, Explain, SampleBadge, moneyFmt } from "../components/cards.js";
import { CapitalReturnVelocityCard } from "../components/CapitalReturnVelocityCard.js";
import { RoicCard } from "../components/RoicCard.js";
import { LeverNeedWarning } from "../components/LeverNeedWarning.js";
import { EquationStrip } from "../components/ExecStory.js";
import { CmGapNotice } from "../components/CmGapNotice.js";
import type { Capabilities, CmGap } from "../lib/card-requirements.js";
import { DataTrustCardNotice } from "../lib/data-trust-banner.js";
import { inventoryTimingLabel } from "../lib/cash-timing-format.js";
import { OverheadHistoryNotice } from "../components/OverheadHistoryNotice.js";
import { useDemoMode } from "../lib/demo-mode.js";

interface LeverField { value: number | null; prior: number | null; status: "live" | "needs_setup"; needs?: string; sample?: number }
interface ProfitModel {
  periodLabel: string;
  windowDays: number;
  /** Server Capabilities passed through whole; only what this page reads is
   *  typed. cmGap set = PARTIAL costing (founder 2026-07-11) → the CM-derived
   *  cards on this page show the warning instead of numbers. */
  capabilities: { contributionMargin: boolean; marketingSpend: boolean; cmGap?: CmGap | null };
  contributionMargin: LeverField;
  capitalReturnVelocity: { months: LeverField; inventoryDays: number | null; settlementDays: number | null; paybackDays: number | null; totalInventoryDays: number | null; totalCrvMonths: number | null; financing: { financedPct: number; ratePct: number } | null };
  profitInflectionPoint: { revenue: LeverField; monthlyRevenue: number; pctAbove: number | null; profitDaysPerMonth: number | null; status: string; needs?: string };
  paybackCurve: Array<{ day: number; cumCmPerCustomer: number; net: number | null }>;
  paybackBreakEvenDay: number | null;
  /** The ONE shared "% of the way to full break-even" (profit basis) — the
   *  equation strip below reuses this exact value when the window hasn't crossed. */
  paybackPctToBreakEven: number | null;
  /** Server verdict on the payback bar's measurability in this window (see
   *  profit-model.ts): null = measurable (an uncrossed line shows "% of the way
   *  to break-even"); otherwise the honest-state copy key. */
  paybackUnmeasurableReason: "needs_full_month" | "before_spend_coverage" | null;
  cashCycle: { dailyCogs: number | null; floatDays: number | null; cashInFloat: number | null; financedInFloat: number | null };
  monthlyOverhead: number | null;
  windowOverhead: number | null;
  financialWindow: { startsAt: string; endsAt: string; days: number; note: string | null; revenueBase: number; revenueGrossBase: number; cmBase: number; marketingSpendBase: number; overheadBase: number | null } | null;
  acquisitionCoverageStart: string | null;
  overheadHistory: {
    recordedMonths: number;
    sparse: boolean;
    trust: "none" | "manual" | "accounting_pending" | "accounting_approved";
    fallbackMonths: number;
  };
  monthly: Array<{ key: string; incomplete: boolean; revenue: number; cm: number; orders: number; customers: number; newCustomers: number; dayOneCm: number; firstOrderCommissions: number; unallocatedCommissions: number; commissions: number; adSpend: number; overhead: number }>;
}

/** The page's PARTIAL-costing gap, or null (complete, or the zero-cost sample
 *  state — the binary caps.contributionMargin=false path stays as it was). */
function cmGapOf(pm: ProfitModel): CmGap | null {
  return pm.capabilities.cmGap && pm.capabilities.cmGap.missingProducts > 0 ? pm.capabilities.cmGap : null;
}

const NEEDS_LABEL: Record<string, string> = {
  contributionMargin: "enter product costs to go live",
  marketingSpend: "add ad spend / commissions to go live",
  overhead: "enter monthly overhead to go live",
  inventory: "enter supplier lead time below to go live",
};

const monthsFmt = (v: number | null): string => (v === null ? "—" : `${v.toFixed(1)} mo`);

export default function FinancialsPage(): React.ReactElement {
  const api = useApiClient();
  const gate = useSetupGate();
  const period = usePeriod();
  const demo = useDemoMode();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const capsQ = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    enabled: ready,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const expenseReady = demo || capsQ.data?.financialBasis === "profit";

  const pmQ = useQuery({
    queryKey: ["profit-model", period.cacheKey],
    queryFn: () => api.get<ProfitModel>("/dashboard/profit-model", { ...period.query }),
    enabled: ready && expenseReady,
  });
  // The flagship Capital-Return-Velocity card fetches the layered payback curve
  // itself (deduped by query key) — the page no longer needs its own copy.
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (capsQ.isLoading) return <p style={{ color: "var(--body)" }}>Checking financial readiness…</p>;
  if (!expenseReady) return <Navigate to="/dashboard" replace />;

  const pm = pmQ.data;
  return (
    <div>
      <div style={kicker}>Financials</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Where's your cash — and how fast does it come back?</h1>
      <p style={{ fontSize: 14.5, color: "var(--body)", maxWidth: 860, lineHeight: 1.55, marginTop: 0 }}>
        Profit and cash are different animals: stock is paid for before it sells, customers cost money before they pay you back.
        This page follows a dollar's round trip — out the door, through the levers, and back.
      </p>

      {!pm && pmQ.isError && (
        <p style={{ marginTop: 20, color: "var(--bad)", fontSize: 13.5 }}>
          Couldn't load the money story — refresh to retry. If it keeps happening, it's on us: use Submit a bug in the sidebar.
        </p>
      )}
      {!pm && !pmQ.isError && <p style={{ marginTop: 20, color: "var(--body)" }}>Working out the money story…</p>}

      {pm && (
        <SortableArea pageKey="financials">
          {/* ── Heroes ── */}
          <Hideable id="fin-operating-profit" label="Operating profit" span={4}>
            <OperatingProfitTile
              pm={pm}
              periodLabel={pm.periodLabel}
            />
          </Hideable>
          <Hideable id="fin-crv" label="Capital return velocity" span={4}>
            <FinTile
              emphasize
              cmGap={cmGapOf(pm)}
              title={`Capital return velocity · ${pm.periodLabel}`}
              field={pm.capitalReturnVelocity.months}
              trustWarning={<DataTrustCardNotice card="Capital Return Velocity" />}
              fmt={(v) => monthsFmt(v)}
              liveSub={
                pm.capitalReturnVelocity.months.value !== null &&
                pm.capitalReturnVelocity.inventoryDays !== null &&
                pm.capitalReturnVelocity.paybackDays !== null
                  ? `${inventoryTimingLabel(pm.capitalReturnVelocity.inventoryDays)} + ${Math.round(pm.capitalReturnVelocity.paybackDays)}d to recover acquisition and overhead${(pm.capitalReturnVelocity.settlementDays ?? 0) >= 1 ? ` + ${Math.round(pm.capitalReturnVelocity.settlementDays!)}d payout lag` : ""}`
                  : "No full cash return yet. Open the full card below to see progress."
              }
              explain={[
                "<b>What it is:</b> How long a dollar stays out of your bank account as it moves through inventory, acquisition, overhead, and payout.",
                "<b>How it's calculated:</b> Your own cash days in inventory + the days for Contribution Margin to recover that completed customer group's acquisition cost and its share of overhead from the same dates + payout lag. Products that tie up more product-cost dollars carry more weight in the inventory time. Product cost is already removed inside Contribution Margin, so inventory is counted as time and never charged twice.",
                "<b>Example (sample data):</b> 26 days in inventory + 1 day to recover acquisition + 36 more days to recover overhead + 6 days for payout = 69 days, or 2.3 months.",
                "<b>Note:</b> Blended. The picker chooses the horizon and the card uses the most recent customer group that fully completed it. If overhead is unconfirmed or a month needs the planning fallback, the card tells you. The lower this is, the sooner you can put the same dollar back to work. Open the full card below for all four stages.",
              ]}
            />
          </Hideable>
          <Hideable id="fin-pip" label="Profit inflection point" span={4}>
            <FinTile
              cmGap={cmGapOf(pm)}
              title="Profit inflection point · current"
              field={pm.profitInflectionPoint.revenue}
              trustWarning={<DataTrustCardNotice card="Profit Inflection Point" />}
              fmt={(v) => (v === null ? "—" : `${moneyFmt(v)}/mo`)}
              liveSub={pipSub(pm)}
              explain={[
                "<b>What it is:</b> The monthly revenue you need to cover overhead and your current acquisition spend. Below it the month loses money. Above it, each extra dollar adds profit.",
                "<b>How it's calculated:</b> Your approved planning overhead plus average monthly acquisition spend from your three most recent complete sales months, divided by the net Contribution Margin rate from those same three months. Revenue has sales tax removed and shipping stays in. If your store gives us one refund total for the whole order, we subtract only the part that belongs to those sales dollars.",
                "<b>Example (sample data):</b> $137,428 per month to break even. At about $202,982 in net monthly revenue, that is roughly 48% above break-even.",
                "<b>Note:</b> Blended. Raising acquisition spend raises the bar. Profit days assume sales arrive evenly through a 30-day month, so they are an easy way to picture the cushion, not actual profitable calendar days.",
              ]}
            />
          </Hideable>

          {/* ── THE flagship Capital-Return-Velocity card — the layered payback
              journey with the CRV-months headline, prominent break-even %, and
              the dollar-days notes footer. ONE component, shared with the Exec
              cockpit; it absorbs the old "cash cycle & payback" card. Kept the
              same Hideable id so saved layouts don't reset. ── */}
          <Hideable id="fin-payback-layers" label="Capital return velocity" span={12}>
            <CapitalReturnVelocityCard anchor="fin-payback-layers" />
          </Hideable>

          {/* ── Profit waterfall (EquationStrip carries its own ⓘ + SAMPLE badging) ── */}
          <Hideable id="fin-waterfall" label="Profit waterfall" span={12}>
            <EquationStrip
              windowDays={pm.financialWindow?.days ?? period.windowDays}
              periodLabel={period.label}
              revenue={pm.financialWindow?.revenueBase}
              cm={pm.capabilities.contributionMargin && pm.financialWindow ? pm.financialWindow.cmBase : null}
              spend={pm.capabilities.marketingSpend && pm.financialWindow ? pm.financialWindow.marketingSpendBase : null}
              windowOverhead={pm.financialWindow?.overheadBase ?? null}
              overheadHistory={pm.overheadHistory}
              basisNote={pm.financialWindow?.note}
              caps={{
                contributionMargin: pm.capabilities.contributionMargin,
                marketingSpend: pm.capabilities.marketingSpend,
                overhead: pm.monthlyOverhead !== null,
                cmGap: pm.capabilities.cmGap ?? null,
              }}
              // The ONE shared break-even % (server paybackPctToBreakEven), shown
              // as a line on the waterfall ONLY while this window hasn't broken
              // even — reusing the flagship's derivation, never a second one.
              pctToBreakEven={pm.paybackBreakEvenDay === null ? pm.paybackPctToBreakEven : null}
            />
          </Hideable>

          {/* ── Monthly trends ── */}
          <Hideable id="fin-op-profit-trend" label="Operating profit by month" span={6}>
            <Card>
              <CardHead
                title="Operating profit by month"
                explain={[
                  "<b>What it is:</b> What each complete month actually earned after every cost — contribution margin minus marketing spend minus overhead. Loss months show as red bars below the line; hiding them would hide the story.",
                  "<b>How it's calculated:</b> Per month: CM after removing the part of each refund that belongs to those sales − (ad spend + affiliate commissions) − that month's overhead. Complete accounting months use reviewed actual expenses. Missing months use the approved planning amount. Manual overhead follows its effective dates.",
                  "<b>Example (sample data):</b> $102K CM − $46K marketing − $22K overhead = $34K operating profit for the month (a month when overhead was still $18K uses $18K, not today's figure).",
                  "<b>Note:</b> Blended, complete months only (the in-progress month would read falsely low). Order-dated, not bank-dated — it's the profit engine's output, not a bank statement.",
                ]}
              />
              <div style={allTimeCaption}>{financialHistoryCaption(pm)}</div>
              <OverheadHistoryNotice history={pm.overheadHistory} />
              <OpProfitTrend pm={pm} />
            </Card>
          </Hideable>
          <Hideable id="fin-rev-vs-breakeven" label="Revenue vs break-even" span={6}>
            <Card>
              <CardHead
                title="Revenue vs true break-even, by month"
                explain={[
                  "<b>What it is:</b> Each month's revenue (bars) against the revenue that month NEEDED to break even (line) — both on the same dollar scale, so bars above the line really are profit months.",
                  "<b>How it's calculated:</b> Break-even line per month = (that month's overhead + that month's marketing spend) ÷ that month's CM%. If your store gives us one refund total for the whole order, we subtract only the part that belongs to the sales dollars CM counts. Complete accounting months use reviewed actual overhead. Missing months use the approved planning amount. Complete sales months only.",
                  "<b>Example (sample data):</b> net revenue $207,720 vs $161,270 needed — the month cleared its bar by about 29%.",
                  "<b>Note:</b> Blended. The line MOVES when you spend more — a heavy testing month raises the bar it has to clear. That's the point. A month with zero or negative margin can't have a break-even line and is skipped with a note.",
                ]}
              />
              <div style={allTimeCaption}>{financialHistoryCaption(pm)}</div>
              <OverheadHistoryNotice history={pm.overheadHistory} />
              <RevVsBreakEven pm={pm} />
            </Card>
          </Hideable>

          {/* Capital return multiple is a planning estimate, not true ROIC.
              Hidden from real clients until it passes founder review. */}
          <Hideable id="fin-roic" label="Capital return multiple" span={6}>
            <RoicCard />
          </Hideable>

          {/* Reinvestment capacity is also parked on Later; kept for saved layouts. */}
          <Hideable id="fin-reinvest" label="Reinvestment capacity" span={6}>
            <Card>
              <CardHead
                title="Reinvestment capacity"
                explain={[
                  "<b>What it is:</b> The honest version of 'how much more can I spend?' — what the machine frees up per month after all costs, and how long an extra acquisition dollar stays gone before returning.",
                  "<b>How it's calculated:</b> Average operating profit of the last three complete months (net CM − marketing − overhead). A whole-order refund subtracts only the part that belongs to the sales dollars CM counts. The return time is your Capital Return Velocity.",
                  "<b>Example (sample data):</b> ≈ $31,000/month freed; an extra dollar of acquisition comes back in ~6 months.",
                  "<b>Note:</b> Blended. We don't see your bank balance (on purpose — no fake runway numbers here); this is the cash your operations generate, which is the ceiling on self-funded growth.",
                ]}
              />
              <div style={allTimeCaption}>Based on your last 3 complete months — not the selected window.</div>
              <ReinvestCapacity pm={pm} />
            </Card>
          </Hideable>

          {/* Cash-timing INPUTS moved off Financials (founder 2026-07-07: "that's
              data we need for calculations, not a card to show") — they live on
              the Costs page (/costs → Cash timing), where cost inputs belong. */}
        </SortableArea>
      )}
    </div>
  );
}

/* ────────────────────────── tiles ────────────────────────── */

/** Three states, all honest: live-with-value (number + context line),
 *  live-without-value ("—" + the reason — loading, unbounded window, or no
 *  break-even in window; NEVER a sample badge for those), and needs_setup
 *  (sample value + SAMPLE badge + what unlocks it). */
function FinTile({ title, field, fmt, liveSub, explain, emphasize, cmGap, trustWarning }: {
  title: string;
  field: LeverField;
  fmt: (v: number | null) => string;
  liveSub?: string;
  explain: string[];
  emphasize?: boolean;
  /** PARTIAL costing → the warning replaces the number (founder 2026-07-11).
   *  The zero-cost needs_setup sample state still takes precedence (cmGap is
   *  only ever set when the account HAS cost inputs). */
  cmGap?: CmGap | null;
  trustWarning?: React.ReactNode;
}): React.ReactElement {
  const needsSetup = field.status === "needs_setup";
  return (
    <Card style={emphasize ? { border: "1px solid var(--accent-2)" } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--body)" }}>
        {title}
        <Explain title={title} lines={explain} />
      </div>
      {trustWarning}
      {needsSetup ? (
        // No-pretend-numbers ruling (founder QA 2026-07-15): an unset input shows the
        // shared amber "needs setup" warning — NEVER a sample number behind a SAMPLE
        // badge (Exec already did this; Financials was the last holdout).
        <div style={{ marginTop: 9 }}>
          <LeverNeedWarning needs={field.needs} compact />
        </div>
      ) : cmGap ? (
        <div style={{ marginTop: 9 }}>
          <CmGapNotice gap={cmGap} />
        </div>
      ) : (
        <>
          <div className="dd-num" style={{ fontSize: 25, fontWeight: 700, color: "var(--num)", margin: "5px 0 3px" }}>{fmt(field.value)}</div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", lineHeight: 1.45 }}>{liveSub ?? ""}</div>
        </>
      )}
    </Card>
  );
}

function pipSub(pm: ProfitModel): string {
  const pip = pm.profitInflectionPoint;
  if (pip.revenue.status !== "live" || pip.pctAbove === null) return "true break-even revenue";
  const pct = Math.round(Math.abs(pip.pctAbove) * 100);
  return pip.pctAbove >= 0
    ? `break-even at current spend · ${pct}% above · about ${pip.profitDaysPerMonth ?? 0} profit days if sales were even`
    : `break-even at current spend · ${pct}% below · this month loses money at the current pace`;
}

/** Operating profit for the window: CM − marketing spend − overhead (server-
 *  totaled over the window, clamped to the real data span). Needs all three inputs
 *  — a profit number missing a cost isn't a profit number — AND resolved queries
 *  (never compute with a cost defaulted to $0 while its query is loading or failed;
 *  review 2026-07-04). 'All time' now fills in (overhead totals over your data span). */
function OperatingProfitTile({ pm, periodLabel }: {
  pm: ProfitModel;
  periodLabel: string;
}): React.ReactElement {
  const hasCm = pm.capabilities.contributionMargin;
  const hasSpend = pm.capabilities.marketingSpend;
  const hasOverhead = pm.monthlyOverhead !== null;
  const needs = !hasCm ? "contributionMargin" : !hasSpend ? "marketingSpend" : !hasOverhead ? "overhead" : undefined;

  let field: LeverField;
  let liveSub: string | undefined;
  if (needs) {
    field = { value: null, prior: null, status: "needs_setup", needs, sample: 18_400 };
  } else if (!pm.financialWindow) {
    field = { value: null, prior: null, status: "live" };
    liveSub = "acquisition costs were not tracked during these dates";
  } else {
    const cm = pm.financialWindow.cmBase;
    const spend = pm.financialWindow.marketingSpendBase;
    const overhead = pm.financialWindow.overheadBase ?? 0;
    const value = cm - spend - overhead;
    field = { value, prior: null, status: "live" };
    liveSub = `${moneyFmt(cm)} CM − ${moneyFmt(spend)} marketing − ${moneyFmt(overhead)} overhead${pm.financialWindow.note ? ` · ${pm.financialWindow.note}` : ""}`;
  }
  return (
    <FinTile
      title={`Operating profit · ${periodLabel}`}
      trustWarning={<DataTrustCardNotice card="Operating profit" />}
      cmGap={cmGapOf(pm)}
      field={field}
      fmt={(v) => (v === null ? "—" : moneyFmt(v))}
      liveSub={liveSub}
      explain={[
        "<b>What it is:</b> What the window actually earned after EVERY cost — variable costs (inside contribution margin), marketing spend, and overhead. The bottom line of the profit engine.",
        "<b>How it's calculated:</b> Contribution margin − marketing spend − overhead across the exact same dates.",
        "<b>Example (sample data):</b> $612K CM − $278K marketing − $264K overhead = $70K operating profit for the year-to-date window.",
        "<b>Note:</b> Blended, since costs, marketing, and overhead are not tracked by product. If your dates reach back before marketing costs were tracked, every part starts at the first covered month. Missing spend never counts as $0. Order-dated, not bank-dated.",
      ]}
    />
  );
}

/* ────────────────────────── monthly charts ────────────────────────── */

/** Money bars (+ optional overlay line) on ONE shared dollar scale, with real
 *  support for NEGATIVE bars (diverging around the $0 line). Built local to
 *  this page because the shared TrendChart clamps at zero (loss months would
 *  silently vanish) and ComboChart puts its line on an independent right axis
 *  (making "bars above the line" visually false) — both review findings.
 */
function MoneyBarsLine({ points, lineName }: {
  points: Array<{ label: string; bar: number; line?: number | null }>;
  lineName?: string;
}): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const n = points.length;
  const W = 640, H = 250, PAD = { l: 8, r: 8, t: 24, b: 30 };
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const vals = [...points.map((p) => p.bar), ...points.map((p) => p.line ?? 0)];
  const vMax = Math.max(10, ...vals) * 1.08;
  const vMin = Math.min(0, ...vals) * 1.08;
  const span = vMax - vMin || 1;
  const y = (v: number): number => PAD.t + ((vMax - v) / span) * ih;
  const slot = iw / n;
  const bw = Math.min(46, slot * 0.62);
  const cx = (i: number): number => PAD.l + i * slot + slot / 2;
  const zeroY = y(0);
  const linePts = points
    .map((p, i) => (p.line === null || p.line === undefined ? null : `${cx(i).toFixed(1)},${y(p.line).toFixed(1)}`))
    .filter((s): s is string => s !== null)
    .join(" ");
  // Sparse month labels so long histories stay readable.
  const every = Math.max(1, Math.ceil(n / 8));

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img"
        aria-label={lineName ? `Monthly bars with ${lineName} line, shared dollar scale` : "Monthly bars, negative months shown below the zero line"}
        onMouseLeave={() => setHover(null)}>
        <line x1={PAD.l} y1={zeroY} x2={W - PAD.r} y2={zeroY} stroke="var(--grid)" />
        {points.map((p, i) => {
          const top = p.bar >= 0 ? y(p.bar) : zeroY;
          const h = Math.max(1.5, Math.abs(y(p.bar) - zeroY));
          const active = hover === i;
          return (
            <g key={p.label} onMouseEnter={() => setHover(i)}>
              <rect x={cx(i) - slot / 2} y={PAD.t} width={slot} height={ih} fill="transparent" />
              <rect x={cx(i) - bw / 2} y={top} width={bw} height={h} rx={2.5}
                fill={p.bar >= 0 ? "var(--good)" : "var(--bad)"} opacity={active ? 1 : 0.8} />
              {(active || n <= 8) && (
                <text x={cx(i)} y={(p.bar >= 0 ? top : top + h) + (p.bar >= 0 ? -5 : 12)} textAnchor="middle" fontSize={9.5}
                  fontWeight={600} fill={p.bar >= 0 ? "var(--num)" : "var(--bad)"} className="dd-num">{moneyFmt(p.bar)}</text>
              )}
              {i % every === 0 && (
                <text x={cx(i)} y={H - 8} textAnchor="middle" fontSize={9.5} fill="var(--text-faint)" className="dd-num">{p.label}</text>
              )}
            </g>
          );
        })}
        {linePts && <polyline points={linePts} fill="none" stroke="var(--accent-2)" strokeWidth={2} strokeLinejoin="round" />}
        {lineName && points.map((p, i) => p.line === null || p.line === undefined ? null : (
          <circle key={`l${p.label}`} cx={cx(i)} cy={y(p.line)} r={hover === i ? 3.6 : 2.6} fill="var(--accent-2)" />
        ))}
        {hover !== null && points[hover]?.line !== null && points[hover]?.line !== undefined && (
          <text x={cx(hover)} y={y(points[hover]!.line!) - 7} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="var(--accent-2)" className="dd-num">
            {moneyFmt(points[hover]!.line!)}
          </text>
        )}
      </svg>
      {lineName && (
        <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 11.5, color: "var(--body)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--good)", display: "inline-block" }} />Revenue</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 3, borderRadius: 2, background: "var(--accent-2)", display: "inline-block" }} />{lineName}</span>
        </div>
      )}
    </div>
  );
}

/** Complete months only — the in-progress month reads falsely low everywhere. */
function completeMonths(pm: ProfitModel): ProfitModel["monthly"] {
  const coveredMonth = pm.acquisitionCoverageStart?.slice(0, 7) ?? null;
  return pm.monthly.filter((month) => !month.incomplete && (!coveredMonth || month.key >= coveredMonth));
}

function financialHistoryCaption(pm: ProfitModel): string {
  if (!pm.acquisitionCoverageStart) return "Every complete month with tracked marketing costs.";
  const label = new Date(pm.acquisitionCoverageStart).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `Complete months since ${label}, when marketing cost tracking starts.`;
}

function OpProfitTrend({ pm }: { pm: ProfitModel }): React.ReactElement {
  const hasAll = pm.capabilities.contributionMargin && pm.capabilities.marketingSpend && pm.monthlyOverhead !== null;
  if (!hasAll) {
    const needs = !pm.capabilities.contributionMargin ? "contributionMargin" : !pm.capabilities.marketingSpend ? "marketingSpend" : "overhead";
    return <SetupNote needs={needs} />;
  }
  // Monthly CM with products missing costs = wrong bars (founder 2026-07-11).
  const cmGap = cmGapOf(pm);
  if (cmGap) return <CmGapNotice gap={cmGap} />;
  const months = completeMonths(pm);
  if (months.length < 2) return <ShortHistoryNote />;
  // Flag 7: each month subtracts the overhead that was ACTUALLY in effect that
  // month (effective-dated from the API), not today's figure smeared backwards.
  const points = months.map((m) => ({ label: m.key, bar: m.cm - (m.adSpend + m.commissions) - m.overhead }));
  return <MoneyBarsLine points={points} />;
}

function RevVsBreakEven({ pm }: { pm: ProfitModel }): React.ReactElement {
  const hasAll = pm.capabilities.contributionMargin && pm.capabilities.marketingSpend && pm.monthlyOverhead !== null;
  if (!hasAll) {
    const needs = !pm.capabilities.contributionMargin
      ? "contributionMargin"
      : !pm.capabilities.marketingSpend
        ? "marketingSpend"
        : "overhead";
    return <SetupNote needs={needs} />;
  }
  // The break-even line divides by each month's CM% — unknowable with products
  // missing costs (founder 2026-07-11). Revenue bars alone aren't the card.
  const cmGap = cmGapOf(pm);
  if (cmGap) return <CmGapNotice gap={cmGap} />;
  const months = completeMonths(pm);
  if (months.length < 2) return <ShortHistoryNote />;
  let skipped = 0;
  const points = months.map((m) => {
    const cmPct = m.revenue > 0 ? m.cm / m.revenue : 0;
    if (cmPct <= 0) {
      skipped += 1;
      return { label: m.key, bar: m.revenue, line: null };
    }
    return { label: m.key, bar: m.revenue, line: (m.overhead + m.adSpend + m.commissions) / cmPct };
  });
  return (
    <div>
      <MoneyBarsLine points={points} lineName="Break-even revenue" />
      {skipped > 0 && (
        <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
          {skipped} month{skipped > 1 ? "s" : ""} had zero-or-negative margin — no break-even line exists for {skipped > 1 ? "those months" : "that month"}.
        </div>
      )}
    </div>
  );
}

/** Reinvestment capacity — avg operating profit of the last 3 COMPLETE months. */
function ReinvestCapacity({ pm }: { pm: ProfitModel }): React.ReactElement {
  const hasAll = pm.capabilities.contributionMargin && pm.capabilities.marketingSpend && pm.monthlyOverhead !== null;
  if (!hasAll) {
    const needs = !pm.capabilities.contributionMargin ? "contributionMargin" : !pm.capabilities.marketingSpend ? "marketingSpend" : "overhead";
    return <SetupNote needs={needs} />;
  }
  // Operating profit built on partial CM would be a wrong number (2026-07-11).
  const cmGap = cmGapOf(pm);
  if (cmGap) return <CmGapNotice gap={cmGap} />;
  const last3 = completeMonths(pm).slice(-3);
  if (last3.length === 0) return <ShortHistoryNote />;
  // Flag 7: each month uses its own era's overhead (effective-dated).
  const monthlyOp = last3.reduce((s, m) => s + (m.cm - (m.adSpend + m.commissions) - m.overhead), 0) / last3.length;
  const crvMonths = pm.capitalReturnVelocity.months.value;
  return (
    <div>
      <div className="dd-num" style={{ fontSize: 25, fontWeight: 700, color: monthlyOp >= 0 ? "var(--num)" : "var(--bad)", margin: "2px 0 4px" }}>
        {moneyFmt(monthlyOp)}<span style={{ fontSize: 14, fontWeight: 600, color: "var(--body)" }}>/mo</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--body)", lineHeight: 1.6 }}>
        {monthlyOp >= 0 ? (
          <>
            That's what the machine frees up each month after every cost (avg of the last {last3.length} complete month{last3.length > 1 ? "s" : ""}) — the honest ceiling on self-funded growth.
            {crvMonths !== null && (
              <> Put an extra dollar into acquisition and it's back in about <b className="dd-num" style={{ color: "var(--num)" }}>{crvMonths.toFixed(1)} months</b> — spend inside that capacity and growth funds itself.</>
            )}
          </>
        ) : (
          <>Operating profit has been negative over the last {last3.length} complete month{last3.length > 1 ? "s" : ""} — the machine is consuming cash, not freeing it. Before adding spend, check the payback curve above for which cost layer to attack first.</>
        )}
      </div>
    </div>
  );
}

function SetupNote({ needs }: { needs: string }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-faint)", fontSize: 12.5, padding: "12px 0" }}>
      <SampleBadge />
      <span>{NEEDS_LABEL[needs] ?? "set this up to see your real numbers"}</span>
    </div>
  );
}

/** History too short — a different problem than a missing input, so it never
 *  says "enter product costs" to someone who already has (review 2026-07-04). */
function ShortHistoryNote(): React.ReactElement {
  return (
    <div style={{ color: "var(--text-faint)", fontSize: 12.5, padding: "12px 0", lineHeight: 1.5 }}>
      Needs at least two complete months of history — this fills in on its own as the months land.
    </div>
  );
}

/* ────────────────────────── styles ────────────────────────── */

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
// Small on-screen disclosure for the monthly-history charts: their series spans
// full history / a fixed run-rate by design, so they don't follow the picker.
const allTimeCaption: React.CSSProperties = { fontSize: 11, color: "var(--body-faint)", marginTop: 2 };
